/**
 * Créer un tarif : le fabricant dont c'est le tarif, et un libellé (« KAESER pièces et service »).
 *
 * Un tarif est un contenant : il reçoit ensuite, chaque année, un fichier Excel par import — chaque
 * fichier devient une VERSION, immuable. Ce panneau ne touche donc à aucun prix.
 */
import { useEffect, useMemo, useState } from 'react';

import { ApiError, api, type Party, type TarifResume } from '../api/client';
import { ComboRecherche, type OptionCombo } from './ComboRecherche';
import { Champ, ErreurFormulaire, Panneau } from './Formulaire';

export function FormulaireTarif({
  onFermer,
  onCree,
}: {
  onFermer: () => void;
  onCree: (tarif: TarifResume) => void;
}) {
  const [fabricants, setFabricants] = useState<Party[]>([]);
  const [fabricantId, setFabricantId] = useState<string | null>(null);
  const [libelle, setLibelle] = useState('');
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    // La liste se charge sans bloquer le formulaire.
    void api.parties(undefined, 'BRAND').then(setFabricants).catch(() => undefined);
  }, []);

  const options = useMemo<OptionCombo[]>(() => fabricants.map((f) => ({ id: f.id, label: f.legalName })), [fabricants]);

  const creer = async () => {
    if (!fabricantId) {
      setErreur(new Error('Choisissez le fabricant dont c’est le tarif.'));
      return;
    }
    if (libelle.trim() === '') {
      setErreur(new Error('Le libellé du tarif est obligatoire.'));
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      onCree(await api.creerTarif({ partyId: fabricantId, label: libelle.trim() }));
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Création impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre="Nouveau tarif"
      sousTitre="Un tarif est un contenant : vous y importerez ensuite le fichier Excel du fabricant, une version par fichier."
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={() => void creer()} disabled={enCours}>
            {enCours ? 'Création…' : 'Créer le tarif'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void creer();
        }}
      >
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        <Champ libelle="Fabricant" requis aide="Le tarif sert à proposer le prix et la désignation des références de CE fabricant.">
          <ComboRecherche
            options={options}
            valeur={fabricantId}
            onChange={setFabricantId}
            placeholder="Chercher un fabricant…"
            viderLibelle="Aucun fabricant choisi"
          />
        </Champ>

        <Champ libelle="Libellé" requis aide="Ce que l’écran affiche comme source : « Tarif KAESER pièces et service ».">
          <input
            type="text"
            autoFocus
            placeholder="KAESER pièces et service"
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
          />
        </Champ>
      </form>
    </Panneau>
  );
}
